import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    productId: Number,
    userId: Number
  }
  
  static targets = ["formButton"]

  async generate(event) {
    event.preventDefault()
    
    const button = event.currentTarget
    const originalText = button.innerHTML
    
    // Disable button and show loading state
    button.disabled = true
    button.innerHTML = '<span class="inline-flex items-center gap-2"><svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Generating...</span>'
    
    try {
      // Use simplified magic link endpoint
      const response = await fetch(`/magic_links`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          user_id: this.userIdValue
        })
      })
      
      const data = await response.json()
      
      if (response.ok && data.success) {
        // Copy to clipboard
        await navigator.clipboard.writeText(data.magic_link)
        
        // Show success message
        this.showNotification('success', `Magic link for ${data.user_name} copied to clipboard!`)
        
        // Reset button after delay
        setTimeout(() => {
          button.disabled = false
          button.innerHTML = originalText
        }, 2000)
      } else {
        throw new Error(data.error || 'Failed to generate magic link')
      }
    } catch (error) {
      console.error('Error generating magic link:', error)
      this.showNotification('error', error.message || 'Failed to generate magic link. Please try again.')
      
      // Reset button
      button.disabled = false
      button.innerHTML = originalText
    }
  }
  
  async generateFromForm(event) {
    event.preventDefault()
    
    // Find the user_id select field in the same form
    const form = event.currentTarget.closest('form')
    const userSelect = form.querySelector('select[name*="user_id"]')
    
    if (!userSelect || !userSelect.value) {
      this.showNotification('error', 'Please select a player first.')
      return
    }
    
    const userId = userSelect.value
    
    const button = event.currentTarget
    const originalText = button.innerHTML
    
    // Disable button and show loading state
    button.disabled = true
    button.innerHTML = '<span class="inline-flex items-center gap-2"><svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Generating...</span>'
    
    try {
      // Use simplified magic link endpoint
      const response = await fetch(`/magic_links`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          user_id: userId
        })
      })
      
      const data = await response.json()
      
      if (response.ok && data.success) {
        // Copy to clipboard
        await navigator.clipboard.writeText(data.magic_link)
        
        // Show success message
        this.showNotification('success', `Magic link for ${data.user_name} copied to clipboard!`)
        
        // Reset button after delay
        setTimeout(() => {
          button.disabled = false
          button.innerHTML = originalText
        }, 2000)
      } else {
        throw new Error(data.error || 'Failed to generate magic link')
      }
    } catch (error) {
      console.error('Error generating magic link:', error)
      this.showNotification('error', error.message || 'Failed to generate magic link. Please try again.')
      
      // Reset button
      button.disabled = false
      button.innerHTML = originalText
    }
  }
  
  showNotification(type, message) {
    // Create notification element
    const notification = document.createElement('div')
    notification.className = `fixed top-4 right-4 z-50 max-w-md rounded-lg p-4 shadow-lg transition-all duration-300 ${
      type === 'success' 
        ? 'bg-green-50 border border-green-200 text-green-800' 
        : 'bg-red-50 border border-red-200 text-red-800'
    }`
    
    notification.innerHTML = `
      <div class="flex items-start gap-3">
        <div class="flex-shrink-0">
          ${type === 'success' 
            ? '<svg class="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path></svg>'
            : '<svg class="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path></svg>'
          }
        </div>
        <div class="flex-1">
          <p class="text-sm font-medium">${message}</p>
        </div>
      </div>
    `
    
    document.body.appendChild(notification)
    
    // Fade in
    setTimeout(() => notification.classList.add('opacity-100'), 10)
    
    // Remove after 5 seconds
    setTimeout(() => {
      notification.classList.remove('opacity-100')
      notification.classList.add('opacity-0')
      setTimeout(() => notification.remove(), 300)
    }, 5000)
  }
}

